<template>
  <div>
    <div v-if="programSupportAdmins.length === 0 && !loading" class="tw-p-5 tw-bg-gray-200 tw-rounded-lg">
      <p class="tw-text-sm tw-text-center">No support admins found</p>

      <SelectGroup
        :options="potentialProgramSupportAdmins"
        label="Add an Admin to this Program"
        name-key="fullName"
        :reduce="user => user"
        v-model="selectedProgramSupportAdmin"
      />
    </div>

    <div v-else class="tw-inline-flex tw-flex-wrap tw-w-full tw-border-b tw-mb-4 tw-pb-4">
      <div class="tw-w-full tw-flex tw-justify-end">
        <SelectGroup
          :options="potentialProgramSupportAdmins"
          label="Add an Admin to this Program"
          name-key="fullName"
          :reduce="user => user"
          v-model="selectedProgramSupportAdmin"
        />
      </div>
      <template v-for="user in programSupportAdmins">
        <div
          :key="user.id"
          class="tw-flex-grow-0 tw-flex-shrink-0 tw-mr-5 tw-pr-5 tw-border-r tw-border-gray-200"
        >
          <UserVertical
            class="tw-px-2 tw-py-1 tw-text-left transition-slow"
            :title="`staff: ${user.fullName}`"
            :user="user"
            userPhotoRadius="tw-rounded-3--force"
            userPhotoSize="tw-w-12 tw-h-12"
            userNameFontClass="tw-text-sm leading-15"
          />

          <BaseButton
            icon
            text=""
            class="tw-mt-2 tw-text-xs tw-text-red-500 tw-border-none tw-rounded-lg hover:tw-bg-red-300"
            confirm
            confirm-message="Are you sure you want to remove this admin?"
            @click="removeSupportAdmin(user.id)"
          >
            <svg slot="icon" class="my-btn__icon" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px"
              y="0px" viewBox="0 0 72 72" style="enable-background:new 0 0 72 72;" xml:space="preserve">
              <path d="M63.1,15.2V64c0,4.4-3.6,8-8,8h-38c-4.4,0-8-3.6-8-8V15.2c0-1.1,0.8-1.9,1.9-1.9s1.9,0.8,1.9,1.9V64c0,2.3,1.9,4.2,4.2,4.2
                h38c2.3,0,4.2-1.9,4.2-4.2V15.2c0-1.1,0.8-1.9,1.9-1.9S63.1,14.2,63.1,15.2z M66.3,4.9H38v-3C38,0.8,37.2,0,36.1,0
                c-1.1,0-1.9,0.8-1.9,1.9v3H5.9C4.7,4.9,4,5.7,4,6.8s0.8,1.9,1.9,1.9h60.2C67.3,8.7,68,8,68,6.8S67.3,4.9,66.3,4.9z M24.7,58.9V17.1
                c0-1.1-0.8-1.9-1.9-1.9s-1.9,0.8-1.9,1.9v41.8c0,1.1,0.8,1.9,1.9,1.9S24.7,60,24.7,58.9z M38,58.9V17.1c0-1.1-0.8-1.9-1.9-1.9
                c-1.1,0-1.9,0.8-1.9,1.9v41.8c0,1.1,0.8,1.9,1.9,1.9C37.2,60.8,38,60,38,58.9z M51.3,58.9V17.1c0-1.1-0.8-1.9-1.9-1.9
                s-1.9,0.8-1.9,1.9v41.8c0,1.1,0.8,1.9,1.9,1.9S51.3,60,51.3,58.9z"/>
            </svg>
          </BaseButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import UserVertical from '@/components/UserVertical.vue';
import SelectGroup from '@/components/SelectGroup.vue';

export default {
  name: 'ProgramSupportAdmins',
  components: {
    UserVertical,
    SelectGroup,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    program: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingProgramUsers: true,
      errorProgramUsers: undefined,
      programSupportAdmins: [],
      programUsers: [],
      selectedProgramSupportAdmin: undefined,
    };
  },
  watch: {
    selectedProgramSupportAdmin(val) {
      if (val) {
        this.nominateSupportAdmin(val);
      }
    },
  },
  computed: {
    potentialProgramSupportAdmins() {
      return this.programUsers.filter((user) => !this.programSupportAdmins.some((admin) => admin.id === user.id));
    },
  },
  methods: {
    async getProgramUsers() {
      this.loadingProgramUsers = true;
      this.errorProgramUsers = null;

      const users = await this.$store.dispatch('getProgramUsers', { progid: this.program.id });

      if (!users) {
        // toast error
        this.errorProgramUsers = 'Error fetching program users';
      } else {
        this.programUsers = users.map((user) => ({
          ...user,
          fullName: `${user.name || ''} ${user.middle || ''} ${user.last_name || ''} [${user.email}]`,
        }));
      }

      this.loadingProgramUsers = false;
    },
    async getProgramSupportAdmins() {
      this.loading = true;

      const programSupportAdmins = await this.$store.dispatch('getProgramSupportAdmins', this.program.id);

      // get users data
      const usersData = await Promise.all(programSupportAdmins.map((user) => this.$store.dispatch('getUserData', user.userid)));

      this.programSupportAdmins = usersData.map((user) => ({
        ...user,
        fullName: `${user.name || ''} ${user.middle || ''} ${user.last_name || ''} [${user.email}]`,
      }));

      this.loading = false;
    },
    async nominateSupportAdmin(admin) {
      await this.$store.dispatch('nominateSupportAdmin', {
        progid: this.program.id,
        userid: admin.id,
      });

      this.selectedProgramSupportAdmin = undefined;

      this.getProgramSupportAdmins();
    },
    async removeSupportAdmin(adminId) {
      await this.$store.dispatch('removeSupportAdmin', {
        progid: this.program.id,
        userid: adminId,
      });

      await this.getProgramSupportAdmins();
    },
  },
  mounted() {
    this.getProgramSupportAdmins();
    this.getProgramUsers();
  },
};
</script>

<style>

</style>
